import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserContext } from "../Context/UserContext";
import { jwtDecode } from "jwt-decode";
import "../styleSheets/foodieNetwork.css";

function FoodieNetwork() {
  const {
    getUsers,
    users,
    sendRequest,
    acceptRequest,
    deleteRequest,
    getFriends,
    friends,
    getUserProfile,
    friendRequests,
    unFriend,
    userDetails,
  } = useContext(UserContext);

  const [searchText, setsearchText] = useState("");
  const [searchResults, setsearchResults] = useState([]);

  const [pageLoading, setPageLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setPageLoading(true);
        await Promise.all([getUsers(), getFriends()]);
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
      }
    };

    loadInitialData();

    // const interval = setInterval(async () => {
    //   await Promise.all([getUsers(), getFriends()]);
    // }, 30000);

    //return () => clearInterval(interval);
  }, []);

  // ---------------- SEARCH ----------------
  useEffect(() => {
    if (!searchText.trim()) {
      setsearchResults([]);
      return;
    }

    const updated = users?.filter((f) =>
      f.name?.toLowerCase().includes(searchText.toLowerCase()),
    );

    setsearchResults(updated || []);
  }, [searchText, users]);

  // ---------------- TOKEN ----------------
  const token = localStorage.getItem("token");
  let myId = "";

  if (token) {
    const decoded = jwtDecode(token);
    myId = decoded.user.id;
  }

  const me = String(myId);
  const friendIds = friends.map((f) => String(f._id));
  const incomingRequestIds = friendRequests.map((r) => String(r._id));

  const updatedUsers = users.filter((u) => {
    const uid = String(u._id);

    const isMe = uid === me;
    const isFriend = friendIds.includes(uid);
    const iSentRequest = u.friendRequests?.some((id) => String(id) === me);
    const theySentRequest = incomingRequestIds.includes(uid);

    return !isMe && !isFriend && !iSentRequest && !theySentRequest;
  });

  // ---------------- UNFRIEND CHECK ----------------
  const canUnfriend = (friendId) => {
    const hasGets = userDetails?.gets?.some(
      (g) =>
        String(g.from?._id) === String(friendId) && g.tStatus === "pending",
    );

    const hasOwes = userDetails?.owes?.some(
      (o) => String(o.to?._id) === String(friendId) && o.tStatus === "pending",
    );

    return !(hasGets || hasOwes);
  };

  // ---------------- PAGE LOADER ----------------
  if (pageLoading) {
    return (
      <div className="page-loader">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div className="network-container">
      {/* ================= DISCOVER SECTION ================= */}
      <div className="network-card discover-section">
        <h4 className="section-title">Discover People</h4>

        <input
          className="network-search"
          type="search"
          placeholder="Search by name..."
          value={searchText}
          onChange={(e) => setsearchText(e.target.value)}
        />

        {/* SUGGESTIONS */}
        {searchText.length === 0 && (
          <>
            <p className="sub-heading">Suggestions</p>

            {updatedUsers.length === 0 ? (
              <div className="empty-state">No Suggestions Available</div>
            ) : (
              updatedUsers.map((e) => (
                <div key={e._id} className="user-row">
                  <Link className="user-link" to={`/friendProfile/${e._id}`}>
                    <div className="user-info">
                      <img
                        src={
                          e.profilePicture?.url ||
                          "https://img.freepik.com/premium-vector/male-face-avatar-icon-set-flat-design-social-media-profiles_1281173-3806.jpg"
                        }
                        className="avatar"
                        alt="profile"
                      />
                      <span className="user-name">{e.name}</span>
                    </div>
                  </Link>

                  <button
                    className="btn-primary-network"
                    disabled={
                      actionLoading?.id === e._id &&
                      actionLoading?.type === "send"
                    }
                    onClick={async () => {
                      if (actionLoading) return;

                      try {
                        setActionLoading({ id: e._id, type: "send" });
                        await sendRequest(e._id);
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                  >
                    {actionLoading?.id === e._id &&
                    actionLoading?.type === "send" ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      "Send Request"
                    )}
                  </button>
                </div>
              ))
            )}
          </>
        )}

        {/* SEARCH RESULTS */}
        {searchText.length !== 0 && (
          <>
            {searchResults.length === 0 ? (
              <div className="empty-state">No Matched Users</div>
            ) : (
              searchResults
                .filter((e) => e._id !== myId)
                .map((e) => (
                  <div key={e._id} className="user-row">
                    <Link className="user-link" to={`/friendProfile/${e._id}`}>
                      <div className="user-info">
                        <img
                          src={
                            e.profilePicture?.url ||
                            "https://img.freepik.com/premium-vector/male-face-avatar-icon-set-flat-design-social-media-profiles_1281173-3806.jpg"
                          }
                          className="avatar"
                          alt="profile"
                        />
                        <span className="user-name">{e.name}</span>
                      </div>
                    </Link>
                  </div>
                ))
            )}
          </>
        )}
      </div>

      {/* ================= LOWER SECTION ================= */}
      <div className="network-bottom">
        {/* FRIEND REQUESTS */}
        <div className="network-card">
          <h4 className="section-title">Friend Requests</h4>

          {friendRequests.length === 0 ? (
            <div className="empty-state">No Requests</div>
          ) : (
            friendRequests.map((e) => (
              <div key={e._id} className="user-row">
                <Link className="user-link" to={`/friendProfile/${e._id}`}>
                  <div className="user-info">
                    <img
                      src={
                        e.profilePicture?.url ||
                        "https://img.freepik.com/premium-vector/male-face-avatar-icon-set-flat-design-social-media-profiles_1281173-3806.jpg"
                      }
                      className="avatar"
                      alt="profile"
                    />
                    <span className="user-name">{e.name}</span>
                  </div>
                </Link>

                <div className="request-actions">
                  <button
                    className="btn-success-network"
                    disabled={
                      actionLoading?.id === e._id &&
                      actionLoading?.type === "accept"
                    }
                    onClick={async () => {
                      if (actionLoading) return;

                      try {
                        setActionLoading({ id: e._id, type: "accept" });
                        await acceptRequest(e._id);
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                  >
                    Accept
                  </button>

                  <button
                    className="btn-danger-network"
                    disabled={
                      actionLoading?.id === e._id &&
                      actionLoading?.type === "delete"
                    }
                    onClick={async () => {
                      if (actionLoading) return;

                      try {
                        setActionLoading({ id: e._id, type: "delete" });
                        await deleteRequest(e._id);
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* FRIENDS */}
        <div className="network-card">
          <h4 className="section-title">Friends</h4>

          {friends.length === 0 ? (
            <div className="empty-state">No Friends Yet</div>
          ) : (
            friends.map((e) => (
              <div key={e._id} className="user-row">
                <Link className="user-link" to={`/friendProfile/${e._id}`}>
                  <div className="user-info">
                    <img
                      src={
                        e.profilePicture?.url ||
                        "https://img.freepik.com/premium-vector/male-face-avatar-icon-set-flat-design-social-media-profiles_1281173-3806.jpg"
                      }
                      className="avatar"
                      alt="profile"
                    />
                    <span className="user-name">{e.name}</span>
                  </div>
                </Link>

                <button
                  className="btn-outline-network"
                  disabled={
                    actionLoading?.id === e._id &&
                    actionLoading?.type === "unfriend"
                  }
                  onClick={async () => {
                    if (actionLoading) return;

                    if (!canUnfriend(e._id)) {
                      alert("Cannot unfriend. Ongoing transactions exist.");
                      return;
                    }

                    try {
                      setActionLoading({ id: e._id, type: "unfriend" });
                      await unFriend(e._id);
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                >
                  {actionLoading?.id === e._id &&
                  actionLoading?.type === "unfriend" ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    "Unfriend"
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default FoodieNetwork;
